import {
  ArrowLeft,
  Droplet,
  AlertCircle,
  Calendar,
  TrendingDown,
  DollarSign,
} from "lucide-react";
import { motion } from "framer-motion";
import { useTheme } from "../context/ThemeContext";
// import { useDateRange } from '../context/DateRangeContext';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Dot,
  Legend,
} from "recharts";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";

import {
  MapPin,
  AlertTriangle,
  Gauge,
  Activity,
  Hash,
  Wifi,
  Battery,
  Clock,
} from "lucide-react";
import { useState, useMemo, useEffect } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { get } from "../Libraries/Ajax/HttpService";
import { ConfigProvider, DatePicker, Progress, theme as antTheme } from "antd";
import { SwapOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import moment from "moment";

export default function MeterLeakDetail({ onBack, meterType }) {
  const { theme, colors } = useTheme();

  const [searchParams] = useSearchParams();
  const category = searchParams.get("category");
  const meterId = searchParams.get("meter_id");
  const timeRange = searchParams.get("time_range");
  const selectedTime = searchParams.get("selected_time");

  const { RangePicker } = DatePicker;

  const navigate = useNavigate();
  const location = useLocation();

  const waterLossGallons = location.state?.water_loss_gallons;
  const price = location.state?.price;

  const today = dayjs().format("YYYY-MM-DD");
  const [initialDetectedDate] = useState(selectedTime);
  const [totalFlow, setTotalFlow] = useState(0);
  const [meterDetails, setMeterDetails] = useState(null);

  const [startDate, setStartDate] = useState(
    selectedTime ? dayjs(selectedTime).format("YYYY-MM-DD") : today,
  );
  const isActive = meterDetails?.m_endpoint_status === "Active";

  const statusColor = isActive ? "#22c55e" : "#ef4444"; // green : red
  const [endDate, setEndDate] = useState(today);

  const [leakData, setLeakData] = useState([]);
  const [apiMessage, setApiMessage] = useState("");
  const leakRateFromState = location.state?.leak_rate;

  const safeRound = (val) => {
    if (val === null || val === undefined) return null; // Return null so we can check for N/A
    const cleaned = String(val).replace(/[$,GPH\s,]/g, "");
    const num = parseFloat(cleaned);
    return isNaN(num) ? null : Math.round(num);
  };
  // Inside MeterLeakDetail.jsx
  const handleBack = () => {
    const previousSubTab = location.state?.fromTab;
    console.log("Navigating back. Previous sub-tab:", previousSubTab);
    if (previousSubTab) {
      // We pass 'activeMainTab' to tell the parent to pick "Data Outliers"
      // and 'activeSubTab' to tell the Outliers component to pick "Outliers By Meter"
      navigate("/alert", {
        state: {
          activeMainTab: "outliers",
          activeSubTab: previousSubTab,
        },
      });
    } else {
      navigate(-1);
    }
  };
  useEffect(() => {
    const fetchAnomalyData = async () => {
      try {
        const response = await get("apis/anoamaly_detection", {
          params: {
            // category: category,
            meter_id: meterId,
            start_date: startDate,
            end_date: endDate,
            time_range: "Custom",
          },
        });

        const payload = response?.data;

        // 3. Extract the array of chart points
        const rootObject = Array.isArray(payload) ? payload[0] : payload || {};
        setApiMessage(rootObject?.message || "");
        const apiDatas = rootObject?.data || [];
        setTotalFlow(rootObject?.total_flow || 0);

        // Extract the meter details (it's an array, so we grab the first item)
        if (rootObject?.meter_details && rootObject.meter_details.length > 0) {
          setMeterDetails(rootObject.meter_details[0]);
        }
        const formattedData = apiDatas.map((item, index) => ({
          id: `${meterId}-${index}`,
          date: item.day,
          gallons: item.total_daily_flow,
          isAnomaly: item.anomaly === 1,
          timestamp: item.day,
          anomalyReason: item.anomaly_reason,
          suppressedReason: item.anomaly_suppressed_reason,
          threshold: safeRound(item.threshold_value),
          rollingThreshold: safeRound(item.upper_rolling_threshold),
          historicalGuardrail: safeRound(item.historical_guardrail_upper),
        }));

        setLeakData(formattedData);
      } catch (error) {
        console.error("Error fetching anomaly data:", error);
      }
    };

    fetchAnomalyData();
  }, [startDate, endDate]);

  const disabledDate = (current) => {
    // Can not select days after today
    return current && current > moment().endOf("day");
  };

  const handleRangeChange = (dates) => {
    if (!dates) return;

    const [start, end] = dates;

    setStartDate(start.format("YYYY-MM-DD"));
    setEndDate(end.format("YYYY-MM-DD"));
  };
  const RenderCustomLegend = () => {
    const legendItems = [
      { label: "Daily Flow", dash: "none", width: 4.5 },
      { label: "Threshold", dash: "12 6", width: 2.5 },
      { label: "Rolling", dash: "1 10", width: 4 },
      { label: "Historical", dash: "15 4 3 4", width: 1.5 },
    ];

    return (
      <div className="flex flex-wrap justify-center gap-3 mb-6">
        {legendItems.map((item, index) => (
          <div key={index} className="flex items-center gap-3">
            <svg width="45" height="12" className="inline-block">
              <line
                x1="0"
                y1="6"
                x2="45"
                y2="6"
                stroke={colors.text} // ✅ THEME COLOR HERE
                strokeWidth={item.width}
                strokeDasharray={item.dash}
                strokeLinecap="round"
              />
            </svg>

            <span
              className="text-[12px] font-bold tracking-wide"
              style={{ color: colors.text }}
            >
              {item.label}
            </span>
          </div>
        ))}
      </div>
    );
  };
  const numDays = useMemo(() => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end.getTime() - start.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(1, diffDays + 1);
  }, [startDate, endDate]);

  const { anomalyGallons, currentGallons, totalWaterLoss, estimatedCost } =
    useMemo(() => {
      if (!leakData.length) {
        return {
          anomalyGallons: 0,
          currentGallons: 0,
          totalWaterLoss: 0,
          estimatedCost: "0.00",
        };
      }

      const anomalyGallons = leakData[0].gallons;
      const currentGallons = leakData[leakData.length - 1].gallons;

      const totalWaterLoss = anomalyGallons - currentGallons;
      const estimatedCost = (totalWaterLoss * 0.005).toFixed(2);

      return {
        anomalyGallons,
        currentGallons,
        totalWaterLoss,
        estimatedCost,
      };
    }, [leakData]);

  let severity;
  let severityColor;

  if (currentGallons < 5000) {
    severity = "critical";
    severityColor = "#ef4444";
  } else if (currentGallons < 10000) {
    severity = "alert";
    severityColor = "#f59e0b";
  } else {
    severity = "minor";
    severityColor = "#10b981";
  }

  const CustomTooltip = ({ active, payload }) => {
    const { theme, colors } = useTheme();

    if (active && payload && payload.length) {
      const data = payload[0].payload;

      // Formatting logic for thresholds
      const formatVal = (val) =>
        val !== null && val !== undefined
          ? `${val.toLocaleString()} Gallons`
          : "N/A";

      return (
        <div
          className="rounded-lg px-4 py-3 border min-w-[240px]"
          style={{
            backgroundColor: theme === "white" ? "#ffffff" : colors.background,
            borderColor: `${colors.primary}30`,
            boxShadow: `0 8px 32px ${colors.primary}30`,
            backdropFilter: "blur(6px)",
          }}
        >
          {/* Date Header */}
          <p
            className="text-xs mb-3 font-bold border-b border-gray-700 pb-2"
            style={{ color: colors.text }}
          >
            {data.timestamp}
          </p>

          {/* Data Rows */}
          <div className="space-y-1.5">
            <div className="flex justify-between gap-4">
              <span
                className="text-[11px]"
                style={{ color: colors.textSecondary }}
              >
                Daily Flow:
              </span>
              <span
                className="text-[11px] font-bold"
                style={{ color: colors.primary }}
              >
                {Math.round(data.gallons).toLocaleString()} Gallons
              </span>
            </div>

            <div className="flex justify-between gap-4">
              <span
                className="text-[11px]"
                style={{ color: colors.textSecondary }}
              >
                Threshold:
              </span>
              <span
                className="text-[11px] font-semibold"
                style={{ color: colors.text }}
              >
                {formatVal(data.threshold)}
              </span>
            </div>

            <div className="flex justify-between gap-4">
              <span
                className="text-[11px]"
                style={{ color: colors.textSecondary }}
              >
                Rolling:
              </span>
              <span
                className="text-[11px] font-semibold"
                style={{ color: colors.text }}
              >
                {formatVal(data.rollingThreshold)}
              </span>
            </div>

            <div className="flex justify-between gap-4">
              <span
                className="text-[11px]"
                style={{ color: colors.textSecondary }}
              >
                Historical:
              </span>
              <span
                className="text-[11px] font-semibold"
                style={{ color: colors.text }}
              >
                {formatVal(data.historicalGuardrail)}
              </span>
            </div>
          </div>

          {/* Anomaly Section */}
          {data.isAnomaly && (
            <div className="mt-3 pt-2 border-t border-red-900/30">
              <div className="flex items-center gap-2 mb-1">
                <AlertTriangle
                  className="w-3 h-3"
                  style={{ color: "#ef4444" }}
                />
                <span
                  className="text-[10px] font-bold uppercase tracking-wider"
                  style={{ color: "#ef4444" }}
                >
                  Anomaly Detected
                </span>
              </div>
              <p
                className="text-[11px] leading-tight"
                style={{ color: "#f87171" }}
              >
                <span className="font-bold">Reason: </span>
                {data.anomalyReason || "Threshold exceeded"}
              </p>
            </div>
          )}

          {/* Suppression Section (Your snippet integrated) */}
          {data.suppressedReason && (
            <div
              className="text-[11px] leading-tight mt-2 pt-2 border-t border-gray-800"
              style={{ color: "#94a3b8" }}
            >
              <span className="font-bold">Suppressed: </span>
              {data.suppressedReason}
            </div>
          )}
        </div>
      );
    }
    return null;
  };

  function InfoTile({ label, value, icon, variant = "default" }) {
    const { colors } = useTheme(); // ✅ get theme colors

    const variantStyles = {
      default: {
        borderColor: `${colors.borderColor}80`,
        backgroundColor: `${colors.background}30`,
      },
      warning: {
        borderColor: `${colors.primary}40`,
        backgroundColor: `${colors.primary}10`,
      },
      danger: {
        borderColor: `${colors.danger}40`,
        backgroundColor: `${colors.danger}10`,
      },
    };

    const textStyles = {
      default: { color: colors.text },
      warning: { color: colors.primary },
      danger: { color: colors.danger },
    };

    return (
      <div
        className="rounded-lg border p-3 transition-colors"
        style={variantStyles[variant]} // ✅ dynamic styles
      >
        <div className="flex items-start gap-3">
          {icon && (
            <div style={{ ...textStyles[variant], opacity: 0.7 }}>{icon}</div>
          )}

          <div className="flex-1 min-w-0">
            <div
              className="text-xs mb-1"
              style={{ color: colors.textSecondary }} //  label color
            >
              {label}
            </div>

            <div className="text-sm" style={textStyles[variant]}>
              {value}
            </div>
          </div>
        </div>
      </div>
    );
  }

  function SectionHeader({ title, icon }) {
    return (
      <div className="flex items-center gap-2 mb-3">
        <div className="text-cyan-400">{icon}</div>
        <h3 className="text-sm text-slate-300">{title}</h3>
      </div>
    );
  }

  return (
    <div
      className="flex-1 overflow-auto"
      style={{ backgroundColor: colors.background }}
    >
      {/* Header */}
      <motion.div
        className="mb-6"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 mb-4 px-4 py-2 rounded-lg border transition-all hover:shadow-md"
          style={{
            backgroundColor: colors.cardBg,
            borderColor: colors.border,
            color: colors.textSecondary,
          }}
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm font-semibold">Back</span>
        </button> */}

        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <button
                onClick={handleBack}
                className="flex items-center transition-all hover:shadow-md"
                style={{
                  borderColor: colors.border,
                  color: colors.textSecondary,
                }}
              >
                <ArrowLeft className="w-9 h-9" />
              </button>

              <h1
                className="text-2xl sm:text-3xl md:text-4xl font-bold text-transparent bg-clip-text mb-2"
                style={{
                  backgroundImage: colors.gradient,
                  WebkitTextFillColor: "transparent",
                  WebkitBackgroundClip: "text",
                }}
              >
                Meter Details
              </h1>
            </div>
            {/* <p className="text-sm" style={{ color: colors.textSecondary }}>
              Meter ID:
              <span className="font-bold" style={{ color: colors.text }}>
                {meterId}
              </span>
              {meterType}
              {waterLossGallons !== null && waterLossGallons !== undefined && (
                <>
                  {", First Leak Detected: "}
                  <span className="font-bold" style={{ color: colors.text }}>
                    {initialDetectedDate
                      ? dayjs(initialDetectedDate).format("MMM DD, YYYY")
                      : "-"}
                  </span>
                </>
              )}
            </p> */}
          </div>
          {/* <div className="flex gap-4 flex-wrap">
            <div
              className="backdrop-blur-sm rounded-xl border p-4 max-[567px]:w-full"
              style={{
                backgroundColor: `${colors.cardBg}0D`,
                borderColor: colors.border,
                minWidth: "180px",
              }}
            >
              <div className="mb-2">
                <span
                  className="text-xs font-semibold"
                  style={{ color: colors.textSecondary }}
                >
                  Total Water Flow
                </span>
              </div>
              <p className="text-xl font-bold" style={{ color: "#fff" }}>
                {safeRound(totalFlow)?.toLocaleString("en-US")} Gallons
               
              </p>
            </div>
           
            {waterLossGallons !== null && waterLossGallons !== undefined && (
              <div
                className="backdrop-blur-sm rounded-xl border p-4 max-[567px]:w-full"
                style={{
                  backgroundColor: `${colors.cardBg}0D`,
                  borderColor: colors.border,
                  minWidth: "180px",
                }}
              >
                <div className="mb-2">
                  <span
                    className="text-xs font-semibold"
                    style={{ color: colors.textSecondary }}
                  >
                    Total Water Loss
                  </span>
                </div>
                <p className="text-xl font-bold" style={{ color: "#ef4444" }}>
                  {safeRound(waterLossGallons)?.toLocaleString("en-US")} Gallons
                 
                </p>
              </div>
            )}
          
            {price !== null && price !== undefined && (
              <div
                className="backdrop-blur-sm rounded-xl border p-4 max-[567px]:w-full"
                style={{
                  backgroundColor: `${colors.cardBg}0D`,
                  borderColor: colors.border,
                  minWidth: "160px",
                }}
              >
                <div className="mb-2">
                  <span
                    className="text-xs font-semibold"
                    style={{ color: colors.textSecondary }}
                  >
                    Estimated Cost
                  </span>
                </div>
                <p className="text-xl font-bold" style={{ color: "#ef4444" }}>
                  {price}${safeRound(price)?.toLocaleString("en-US")}
                </p>
              </div>
            )}
           
            {leakRateFromState && (
              <div
                className="backdrop-blur-sm rounded-xl border p-4 max-[567px]:w-full"
                style={{
                  backgroundColor: `${colors.cardBg}0D`,
                  borderColor: colors.border,
                  minWidth: "200px",
                }}
              >
                <div className="flex items-center gap-2 mb-2">
                  <AlertCircle
                    className="w-4 h-4 flex-shrink-0"
                    style={{ color: "#FF9800" }}
                  />
                  <span
                    className="text-xs font-semibold"
                    style={{ color: colors.textSecondary }}
                  >
                    Message
                  </span>
                </div>

                <p className="text-xs mt-6" style={{ color: colors.text }}>
                 
                  Leak: {leakRateFromState}
                </p>
              </div>
            )}
          </div> */}
        </div>
      </motion.div>

      {/* Chart Section */}
      <div className="grid grid-cols-1 lg:grid-cols-[3fr_1fr] gap-6 mb-5">
        {/* LEFT COLUMN (Chart Card) */}
        <motion.div
          className="min-w-0 backdrop-blur-sm rounded-xl border p-6"
          style={{ backgroundColor: colors.cardBg, borderColor: colors.border }}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <div className="max-[600px]:overflow-x-auto">
            <div className="mb-6">
              <div className="flex items-center justify-between mb-4 flex-wrap gap-5">
                <h2
                  className="text-xl font-bold"
                  style={{ color: colors.primary }}
                >
                  Anomaly Detection Details
                </h2>

                {/* Date Range */}
                <div className="flex items-center gap-4">
                  <div style={{ width: "100%" }}>
                    <label
                      className="block text-xs mb-2 font-semibold"
                      style={{ color: colors.primary }}
                    >
                      Date Range
                    </label>

                    <ConfigProvider
                      theme={{
                        algorithm:
                          theme === "white"
                            ? antTheme.defaultAlgorithm
                            : antTheme.darkAlgorithm,
                        token: {
                          colorPrimary: colors.primary,
                          colorBgContainer: colors.background,
                          colorBorder: colors.border,
                          colorText: colors.text,
                        },
                      }}
                    >
                      <RangePicker
                        value={
                          startDate && endDate
                            ? [dayjs(startDate), dayjs(endDate)]
                            : null
                        }
                        onChange={handleRangeChange}
                        disabledDate={disabledDate}
                        separator={
                          <SwapOutlined
                            style={{ color: colors.textSecondary }}
                          />
                        }
                        style={{
                          backgroundColor: colors.background,
                          border: `1px solid ${colors.border}`,
                          color: colors.text,
                          padding: "0.5rem 1rem",
                          borderRadius: "0.5rem",
                          height: "42px",
                          width: "100%",
                        }}
                        popupClassName={`mizu-calendar-popup calendar-${theme}`}
                      />
                    </ConfigProvider>
                  </div>
                </div>
              </div>

              <p className="text-sm" style={{ color: colors.textSecondary }}>
                Anomaly detected over {numDays} {numDays === 1 ? "day" : "days"}
              </p>
            </div>

            {/* Chart */}
            <div className="w-full max-[600px]:w-[700px]">
              <div style={{ width: "100%", height: "500px" }}>
                {leakData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={leakData}
                      margin={{ top: 0, right: 30, left: 20, bottom: 60 }}
                    >
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke={colors.border}
                      />

                      <XAxis
                        dataKey="date"
                        tick={{ fill: colors.textSecondary, fontSize: 11 }}
                        stroke={colors.border}
                      />

                      <YAxis
                        label={{
                          value: "Gallons",
                          angle: -90,
                          position: "insideLeft",
                          offset: 1,
                          fill: colors.textSecondary,
                          fontSize: 12,
                        }}
                        width={80}
                        tick={{ fill: colors.textSecondary, fontSize: 11 }}
                        tickFormatter={(value) => value.toLocaleString("en-US")}
                        stroke={colors.border}
                      />

                      <Tooltip content={<CustomTooltip />} />
                      <Legend
                        content={<RenderCustomLegend />}
                        verticalAlign="top"
                        height={50}
                      />

                      {/* Thresholds */}
                      <Line
                        type="monotone"
                        dataKey="threshold"
                        stroke={colors.textSecondary}
                        strokeWidth={2}
                        strokeDasharray="12 8"
                        dot={false}
                        connectNulls
                      />

                      <Line
                        type="monotone"
                        dataKey="rollingThreshold"
                        stroke={colors.textSecondary}
                        strokeWidth={2}
                        strokeDasharray="1 12"
                        strokeLinecap="round"
                        dot={false}
                        connectNulls
                      />

                      <Line
                        type="monotone"
                        dataKey="historicalGuardrail"
                        stroke={colors.textSecondary}
                        strokeWidth={2}
                        strokeDasharray="15 5 3 5"
                        dot={false}
                        connectNulls
                      />

                      {/* Main Line */}
                      <Line
                        type="monotone"
                        dataKey="gallons"
                        stroke={colors.primary}
                        strokeWidth={2}
                        dot={(props) => {
                          const { cx, cy, payload } = props;
                          if (payload.isAnomaly) {
                            return (
                              <Dot
                                cx={cx}
                                cy={cy}
                                r={6}
                                fill="#ef4444"
                                stroke="#fff"
                                strokeWidth={2}
                              />
                            );
                          }
                          return (
                            <Dot
                              cx={cx}
                              cy={cy}
                              r={2.5}
                              fill={colors.primary}
                            />
                          );
                        }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div
                    className="h-full w-full flex flex-col items-center justify-center border-2 border-dashed rounded-xl"
                    style={{
                      borderColor: colors.border,
                      backgroundColor: `${colors.cardBg}80`,
                    }}
                  >
                    <Clock
                      className="w-12 h-12 mb-4 opacity-20"
                      style={{ color: colors.primary }}
                    />
                    <p
                      className="text-lg font-medium text-center px-4"
                      style={{ color: colors.textSecondary }}
                    >
                      {apiMessage || "No data available for this range"}
                    </p>
                    {meterDetails?.latest_flow_time && (
                      <p
                        className="mt-2 text-sm"
                        style={{ color: colors.primary }}
                      >
                        Last recorded flow: {meterDetails.latest_flow_time}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </motion.div>

        {/* RIGHT COLUMN (Stats / Summary Panel) */}
        <motion.div
          className="min-w-0 backdrop-blur-sm rounded-xl h-[700px] overflow-y-auto"
          style={{
            backgroundColor: colors.cardBg,
            borderColor: colors.border,
            height: "675px",
          }}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <Card
            className="shadow-lg"
            style={{
              backgroundColor: colors.cardBg,
              borderColor: colors.border,
            }}
          >
            <CardHeader
              className="border-b"
              style={{ borderColor: colors.border }}
            >
              <CardTitle
                className="text-cyan-400 font-bold"
                style={{ fontSize: "20px", color: colors.primary }}
              >
                Meter Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6 pt-0">
              {/* Meter ID and First Leak Detected */}
              <div className="grid grid-cols-1 gap-3">
                <div
                  className="backdrop-blur-sm rounded-xl border p-4 flex items-start gap-3"
                  style={{
                    backgroundColor: `${colors.cardBg}0D`,
                    borderColor: `${colors.primary}40`,
                  }}
                >
                  <div className="mt-1 text-cyan-400">
                    <Hash size={16} />
                  </div>

                  <div>
                    <span
                      className="text-xs font-semibold"
                      style={{ color: colors.textSecondary }}
                    >
                      Meter ID
                    </span>
                    <p className="text-sm mt-1" style={{ color: colors.text }}>
                      {meterId}
                    </p>
                  </div>
                </div>

                {/* First Leak Detected */}
                <div
                  className="backdrop-blur-sm rounded-xl border p-4 flex items-start gap-3"
                  style={{
                    backgroundColor: `${colors.warning || "#FF9800"}10`,
                    borderColor: `${colors.warning || "#FF9800"}50`,
                  }}
                >
                  <div className="mt-1 text-yellow-400">
                    <AlertTriangle size={16} />
                  </div>

                  <div>
                    <span
                      className="text-xs font-semibold"
                      style={{ color: colors.textSecondary }}
                    >
                      First Leak Detected
                    </span>
                    <p className="text-sm mt-1 text-yellow-300">
                      <span
                        className="font-bold"
                        style={{ color: colors.warning || "#FF9800" }}
                      >
                        {initialDetectedDate
                          ? dayjs(initialDetectedDate).format("MMM DD, YYYY")
                          : "-"}
                      </span>
                    </p>
                  </div>
                </div>

                {/*
                 */}
                <div
                  className="backdrop-blur-sm rounded-xl border p-4 flex items-start gap-3 max-[567px]:w-full"
                  style={{
                    backgroundColor: `${colors.cardBg}0D`,
                    borderColor: `${colors.primary}40`,
                    minWidth: "180px",
                  }}
                >
                  {/* LEFT ICON */}
                  <div className="mt-1 text-cyan-400">
                    <Droplet size={18} />
                  </div>

                  {/* CONTENT */}
                  <div className="flex-1">
                    <div className="mb-1">
                      <span
                        className="text-xs font-semibold"
                        style={{ color: colors.textSecondary }}
                      >
                        Total Water Flow
                      </span>
                    </div>

                    <p
                      className="text-sm font-bold"
                      style={{ color: "#ffffff" }}
                    >
                      {safeRound(totalFlow)?.toLocaleString("en-US")} Gallons
                    </p>
                  </div>
                </div>

                <div
                  className="backdrop-blur-sm rounded-xl border p-4 flex items-start gap-3 max-[567px]:w-full"
                  style={{
                    backgroundColor: `${colors.cardBg}0D`,
                    borderColor: `${colors.primary}40`,
                    minWidth: "180px",
                  }}
                >
                  {/* LEFT ICON */}
                  <div className="mt-1 text-red-400">
                    <TrendingDown size={16} />
                  </div>

                  {/*Total Water Loss */}
                  <div className="flex-1">
                    <div className="mb-1">
                      <span
                        className="text-xs font-semibold"
                        style={{ color: colors.textSecondary }}
                      >
                        Total Water Loss
                      </span>
                    </div>

                    <p
                      className="text-sm font-bold"
                      style={{ color: "#ef4444" }}
                    >
                      {safeRound(waterLossGallons)?.toLocaleString("en-US")}{" "}
                      Gallons
                    </p>
                  </div>
                </div>

                {/* Estimated cost */}
                {price !== null && price !== undefined && (
                  <div
                    className="backdrop-blur-sm rounded-xl border p-4 flex items-start gap-3 max-[567px]:w-full"
                    style={{
                      backgroundColor: `${colors.cardBg}0D`,
                      borderColor: `${colors.primary}40`,
                      minWidth: "160px",
                    }}
                  >
                    {/* LEFT ICON */}
                    <div className="mt-1 text-red-400">
                      <DollarSign size={16} />
                    </div>

                    {/* CONTENT */}
                    <div className="flex-1">
                      <div className="mb-1">
                        <span
                          className="text-xs font-semibold"
                          style={{ color: colors.textSecondary }}
                        >
                          Estimated Cost
                        </span>
                      </div>

                      <p
                        className="text-sm font-bold"
                        style={{ color: "#ef4444" }}
                      >
                        ${safeRound(price)?.toLocaleString("en-US")}
                      </p>
                    </div>
                  </div>
                )}

                {/* Message */}
                {leakRateFromState && (
                  <div
                    className="backdrop-blur-sm rounded-xl border p-4 flex items-start gap-3 max-[567px]:w-full"
                    style={{
                      backgroundColor: `${colors.cardBg}0D`,
                      borderColor: `${colors.primary}40`,
                      minWidth: "200px",
                    }}
                  >
                    {/* LEFT ICON */}
                    <div className="mt-1 text-yellow-400">
                      <AlertCircle size={18} />
                    </div>

                    {/* CONTENT */}
                    <div className="flex-1">
                      <div className="mb-1">
                        <span
                          className="text-xs font-semibold"
                          style={{ color: colors.textSecondary }}
                        >
                          Message
                        </span>
                      </div>

                      <p
                        className="text-sm font-medium"
                        style={{ color: colors.text }}
                      >
                        Leak: {leakRateFromState}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Location Section */}
              <div>
                <SectionHeader title="Location" icon={<MapPin size={18} />} />

                <div
                  className="backdrop-blur-sm rounded-xl border p-4 flex items-start gap-3 mt-3 max-[567px]:w-full"
                  style={{
                    backgroundColor: `${colors.cardBg}0D`,
                    borderColor: `${colors.primary}40`,
                  }}
                >
                  {/* LEFT ICON */}
                  <div className="mt-1 text-cyan-400">
                    {/* <MapPin size={18} /> */}
                  </div>

                  {/* CONTENT */}
                  <div className="flex-1">
                    <div className="mb-1">
                      <span
                        className="text-xs font-semibold"
                        style={{ color: colors.textSecondary }}
                      >
                        Service Address
                      </span>
                    </div>

                    <p
                      className="text-sm leading-relaxed"
                      style={{ color: colors.text }}
                    >
                      {/* 11311 1/2 W Lake Houston Pkwy, Well Meter, Houston, TX 77044 */}
                      {meterDetails?.service_address || "N/A"}
                    </p>
                  </div>
                </div>
              </div>

              {/* Status Section */}
              <div>
                <SectionHeader
                  title="Status Alerts"
                  icon={<AlertTriangle size={18} />}
                />

                <div className="grid grid-cols-1 gap-3 mt-3">
                  {/* Backflow */}
                  <div
                    className="backdrop-blur-sm rounded-xl border p-4 flex items-start gap-3"
                    style={{
                      backgroundColor: `${statusColor}10`,
                      borderColor: `${statusColor}50`,
                    }}
                  >
                    <div className="mt-1" style={{ color: statusColor }}>
                      <Activity size={18} />
                    </div>

                    <div>
                      {/* <span className="text-xs font-semibold" style={{ color: colors.textSecondary }}>
                        Backflow Detected
                      </span> */}
                      <p
                        className="text-sm font-medium mt-1"
                        style={{ color: statusColor }}
                      >
                        {/* 2,784,230,912 gallons (last 30 days) */}
                        {meterDetails?.m_endpoint_status}
                      </p>
                    </div>
                  </div>

                  {/* Leak */}
                  {/* <div className="backdrop-blur-sm rounded-xl border p-4 flex items-start gap-3"
                    style={{
                      backgroundColor: `${colors.warning || "#FF9800"}10`,
                      borderColor: `${colors.warning || "#FF9800"}50`,
                    }}
                  >
                    <div className="mt-1" style={{ color: colors.warning || "#FF9800" }}>
                      <AlertTriangle size={18} />
                    </div>

                    <div>
                      <span className="text-xs font-semibold" style={{ color: colors.textSecondary }}>
                        Leak Detected
                      </span>
                      <p className="text-sm font-medium" style={{ color: colors.warning || "#FF9800" }}>
                        6,000 gallons/hour since Mar 13, 2026 04:00
                      </p>
                    </div>
                  </div> */}
                </div>
              </div>
              {/* Meter Info Section */}
              <div>
                <SectionHeader
                  title="Meter Details"
                  icon={<Gauge size={18} />}
                />

                <div className="grid grid-cols-1 gap-3 mt-3">
                  {/* Install Date */}
                  <div
                    className="backdrop-blur-sm rounded-xl border p-4 flex items-start gap-3"
                    style={{
                      backgroundColor: `${colors.cardBg}0D`,
                      borderColor: `${colors.primary}40`,
                    }}
                  >
                    <div className="mt-1 text-cyan-400">
                      <Calendar size={16} />
                    </div>

                    <div>
                      <span
                        className="text-xs font-semibold"
                        style={{ color: colors.textSecondary }}
                      >
                        Meter Install Date
                      </span>
                      <p
                        className="text-sm mt-1"
                        style={{ color: colors.text }}
                      >
                        {meterDetails?.meter_install_date || "N/A"}
                      </p>
                    </div>
                  </div>

                  {/* Serial */}
                  <div
                    className="backdrop-blur-sm rounded-xl border p-4 flex items-start gap-3"
                    style={{
                      backgroundColor: `${colors.cardBg}0D`,
                      borderColor: `${colors.primary}40`,
                    }}
                  >
                    <div className="mt-1 text-cyan-400">
                      <Hash size={16} />
                    </div>

                    <div>
                      <span
                        className="text-xs font-semibold"
                        style={{ color: colors.textSecondary }}
                      >
                        Meter S/N
                      </span>
                      <p
                        className="text-sm mt-1"
                        style={{ color: colors.text }}
                      >
                        {meterDetails?.meter_sn || "N/A"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Endpoint Info Section */}
              <div>
                <SectionHeader
                  title="Endpoint Details"
                  icon={<Activity size={18} />}
                />

                <div className="grid grid-cols-1 gap-3 mt-3">
                  <div
                    className="backdrop-blur-sm rounded-xl border p-4 flex items-start gap-3"
                    style={{
                      backgroundColor: `${colors.cardBg}0D`,
                      borderColor: `${colors.primary}40`,
                    }}
                  >
                    <div className="mt-1 text-cyan-400">
                      <Hash size={16} />
                    </div>

                    <div>
                      <span
                        className="text-xs font-semibold"
                        style={{ color: colors.textSecondary }}
                      >
                        Endpoint S/N
                      </span>
                      <p
                        className="text-sm mt-1"
                        style={{ color: colors.text }}
                      >
                        {meterDetails?.endpoint_sn || "N/A"}
                      </p>
                    </div>
                  </div>

                  <div
                    className="backdrop-blur-sm rounded-xl border p-4 flex items-start gap-3"
                    style={{
                      backgroundColor: `${colors.cardBg}0D`,
                      borderColor: `${colors.primary}40`,
                    }}
                  >
                    <div className="mt-1 text-cyan-400">
                      <Calendar size={16} />
                    </div>

                    <div>
                      <span
                        className="text-xs font-semibold"
                        style={{ color: colors.textSecondary }}
                      >
                        Endpoint Install Date
                      </span>
                      <p
                        className="text-sm mt-1"
                        style={{ color: colors.text }}
                      >
                        {meterDetails?.endpoint_install_date || "N/A"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* System Status Section */}
              <SectionHeader
                title="System Status"
                icon={<Activity size={18} />}
              />

              <div
                className="backdrop-blur-sm rounded-xl border p-4 flex items-start gap-3"
                style={{
                  backgroundColor: `${colors.cardBg}0D`,
                  borderColor: `${colors.primary}40`,
                }}
              >
                <div className="mt-1 text-cyan-400">
                  <Clock size={16} />
                </div>

                <div>
                  <span
                    className="text-xs font-semibold"
                    style={{ color: colors.textSecondary }}
                  >
                    Last Communication
                  </span>
                  <p className="text-sm mt-1" style={{ color: colors.text }}>
                    {meterDetails?.last_comm_time}
                  </p>
                </div>
              </div>
              {/* Add this inside <CardContent> in the right column */}
              <div
                className="backdrop-blur-sm rounded-xl border p-4 flex items-start gap-3 mt-3"
                style={{
                  backgroundColor: `${colors.cardBg}0D`,
                  borderColor: `${colors.primary}40`,
                }}
              >
                <div className="mt-1 text-cyan-400">
                  <Clock size={16} />
                </div>
                <div>
                  <span
                    className="text-xs font-semibold"
                    style={{ color: colors.textSecondary }}
                  >
                    Latest Flow Time
                  </span>
                  <p className="text-sm mt-1" style={{ color: colors.text }}>
                    {meterDetails?.latest_flow_time || "N/A"}
                  </p>
                </div>
              </div>
              
             
              <div className="grid grid-cols-2 gap-3">
                <div
                  className="backdrop-blur-sm rounded-xl border p-4"
                  style={{
                    backgroundColor: colors.cardBg,
                    borderColor: colors.border,
                  }}
                >
                  <div className="flex items-center gap-2 mb-1 text-green-400">
                    <Battery size={16} />
                    <span className="text-[10px] font-bold  text-slate-400">
                      Battery
                    </span>
                  </div>
                  <p
                    className="text-sm font-bold capitalize"
                    style={{ color: colors.text }}
                  >
                    {meterDetails?.battery_level || "N/A"}
                  </p>
                </div>
                <div
                  className="backdrop-blur-sm rounded-xl border p-4"
                  style={{
                    backgroundColor: colors.cardBg,
                    borderColor: colors.border,
                  }}
                >
                  <div className="flex items-center gap-2 mb-1 text-cyan-400">
                    <Wifi size={16} />
                    <span className="text-[10px] font-bold  text-slate-400">
                      Signal Strength
                    </span>
                  </div>
                  <p
                    className="text-sm font-bold capitalize"
                    style={{ color: colors.text }}
                  >
                     {meterDetails?.signal_strength || "N/A"}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
}
